/**
 * Games Info Model
 */

define(function(require, exports, module) {
    'use strict';

    var _ = require('underscore');
    var $ = require('j');
    var util = require('utils');
    var PortletModel = require('modules/portlet/portlet.model');
    var user = require('user');
    var core = require('core');
    var config = require('config');

    var url = require('util/url.util');
    var device = require('util/device.util');
    var historyUtil = require('util/history.util');

    var GamesInfoView = require('portlets/games-info/gamesinfo.view');
    var GamesInfoGameCollection = require('portlets/games-info/gamesinfo.game.collection');
    var CategoryCollection = require('portlets/games-info/categories/gamesinfo.category.collection');

    var segmentationHelper = require('modules/segmentation/segmentation.helper');

    module.exports = PortletModel.extend({

        defaults: {
            sortField: 'default',
            isNeedZIndex: true
        },

        initialize: function() {
            PortletModel.prototype.initialize.apply(this, arguments);

            this.games = new GamesInfoGameCollection(this.get('games'));
            this.games.fetch();
            this.categories = new CategoryCollection(this.get('categories'), {
                allGames: this.games
            });
        },

        getCategoryGamesOrFilterThemByCMS: function(category) {
            return _.filter(category.games, function(gameCode) {
                var gameByCode = _.findWhere(this.get('games'), { 'gameCode': gameCode });
                if (device.formFactor === 'desktop') {
                    return gameByCode;
                }
                var gameByMobileCode = _.findWhere(this.get('games'), { 'mobileGameCode': gameCode });
                return gameByMobileCode || gameByCode;
            }.bind(this));
        },

        close: function() {
            PortletModel.prototype.close.apply(this, arguments);

            this.categories.close();
        },

        getView: GamesInfoView,

        parse: function(response) {
            var preferences = response.config;
            var isLoggedIn = user.isLoggedIn();
            var categories;
            var activeCatName;
            var locationCatName;
            var activeFeed;

            var parseGameConfig = function(games) {
                var errors = [];
                var warnings = [];

                _.each(games, function(gameCfg) {
                    var mode = gameCfg.funSupport;
                    var fallBackImageUrl = gameCfg.imageURLs && gameCfg.imageURLs.c1x1;

                    gameCfg.isShowDemoButton = !(mode === 'unavailable' || mode === 'guest' && user.isLoggedIn());

                    if (!fallBackImageUrl) {
                        errors.push(gameCfg);
                        return;
                    }

                    // ensure non-null objects
                    [
                        'imageURLs',
                        'mobileImageURLs',
                        'animatedImageURLs',
                        'guestImageURLs',
                        'guestMobileImageURLs',
                        'guestAnimatedImageURLs'
                    ].forEach(function(imgObjName) {
                        gameCfg[imgObjName] = gameCfg[imgObjName] || {};
                    });

                    // hack: mobileImageUrl.1x1 is required (should be an error)
                    if (!gameCfg.mobileImageURLs.c1x1) {
                        warnings.push(gameCfg);
                        gameCfg.mobileImageURLs.c1x1 = fallBackImageUrl;
                    }

                    // ensure filled tiles with base assets
                    var imageNames = ['c1x2', 'c2x1', 'c2x2'];
                    imageNames.forEach(function(imageName) {
                        if (!gameCfg.imageURLs[imageName]) {
                            gameCfg.imageURLs[imageName] = fallBackImageUrl;
                        }
                        if (!gameCfg.mobileImageURLs[imageName]) {
                            gameCfg.mobileImageURLs[imageName] = gameCfg.imageURLs[imageName];
                        }
                    });

                    imageNames.unshift('c1x1');

                    // apply guest assets if needed
                    if (!isLoggedIn) {
                        imageNames.forEach(function(imageName) {
                            if (gameCfg.guestImageURLs[imageName]) {
                                gameCfg.imageURLs[imageName] = gameCfg.guestImageURLs[imageName];
                            }
                            if (gameCfg.guestMobileImageURLs[imageName]) {
                                gameCfg.mobileImageURLs[imageName] = gameCfg.guestMobileImageURLs[imageName];
                            }
                            if (gameCfg.guestAnimatedImageURLs[imageName]) {
                                gameCfg.animatedImageURLs[imageName] = gameCfg.guestAnimatedImageURLs[imageName];
                            }
                        });
                    }

                    // trim paths of spaces
                    imageNames.forEach(function(imageName) {
                        gameCfg.imageURLs[imageName] = gameCfg.imageURLs[imageName].trim();
                        gameCfg.mobileImageURLs[imageName] = gameCfg.mobileImageURLs[imageName].trim();
                        if (gameCfg.animatedImageURLs[imageName]) {
                            gameCfg.animatedImageURLs[imageName] = gameCfg.animatedImageURLs[imageName].trim();
                        }
                    });
                });

                if (errors.length > 0) {
                    core.error('Game Images Config Not Found for ' + errors.length + ' games', errors);
                }

                if (warnings.length > 0) {
                    core.warn('Incorrect game configuration in ' + warnings.length + ' games', warnings);
                }
            };

            var getGamesFromConfigurationOrByGameTypes = function() {
                var games = _.toArray(config.get('gamesConfiguration'));
                var gameTypes = preferences.gameTypes;

                var _filterGamesByGameTypesFromCMS = function() {
                    return _.filter(games, function(game) {
                        var gameType = device.formFactor === 'desktop'
                            ? game.gameType
                            : game.mobileGameType;

                        return gameTypes.includes(gameType);
                    });
                };

                if (!_.isEmpty(gameTypes)) {
                    games = _filterGamesByGameTypesFromCMS();
                }

                parseGameConfig(games);

                return games;
            };

            if (device.formFactor === 'mobile') {
                preferences.navigationPosition = 'TOP';
            }

            // Handle showOnly: leave only 1 category for showOnly
            if (preferences.showOnly && preferences.showOnlyType && preferences.showOnlyType !== 'ALL') {
                var catLayout;
                if (user.isLoggedIn()) {
                    switch (preferences.showOnlyType) {
                        case 'RECENTLY_PLAYED':
                            activeCatName = 'recentlyplayed';
                            catLayout = preferences.recentlyLayout;
                            break;
                        case 'FAVORITES':
                            activeCatName = 'favorites';
                            catLayout = preferences.favoritesLayout;
                            break;
                    }
                }

                if (activeCatName) {
                    categories = [];
                    this._addCategory(categories, activeCatName, { customLayout: catLayout });
                } else {
                    throw new Error('Data for games portlet is invalid');
                }
            } else {
                if (!preferences.categoriesFeedName) {
                    throw new Error('No game name configured');
                }

                // user segmentation
                activeFeed = this._selectActiveFeed(preferences);

                activeFeed.categories = activeFeed.categories || [];

                // remap properties
                activeFeed.categories.forEach(function(category) {
                    category.games = category.gameCodes;
                    category.translation = (activeFeed.translation || activeFeed.translationKeyPrefixMob) + '.'
                        + category.name;
                });

                // Filter categories
                categories = this._filterCategories(activeFeed.categories);

                // add special categories
                if (user.isLoggedIn()) {
                    if (preferences.useRecentlyEnabled) {
                        this._addCategory(categories, 'recentlyplayed', {
                            dynamic: true,
                            customLayout: preferences.recentlyLayout
                        });
                    }

                    if (preferences.includeFavoritesCategory && preferences.useFavoritesEnabled) {
                        this._addCategory(categories, 'favorites', {
                            dynamic: true,
                            atStart: preferences.favoritesLocation === 'FIRST',
                            customLayout: preferences.favoritesLayout
                        });
                    }
                }

                // Set activeCategory
                activeCatName = (_.find(categories, function(category) {
                    return !category.dynamic;
                }) || categories[0] || {}).name;

                locationCatName = this.getCategoryLocation();
                if (locationCatName) {
                    if (this.getCategory(locationCatName, categories)) {
                        activeCatName = locationCatName;
                    } else {
                        this.setCategoryLocation(activeCatName);
                    }
                }
            }

            categories.forEach(function(category) {
                category.active = category.name === activeCatName;
                category.gamesInitialOrder = category.dynamic
                    ? []
                    : _.clone(category.games);
            });

            return _.chain(response)
                .extend(preferences, {
                    games: getGamesFromConfigurationOrByGameTypes(),
                    categories: categories,
                    categoriesFeed: activeFeed
                })
                .omit('config', 'profileFeeds')
                .value();
        },

        /**
         * Select active feed based on user segmentation settings
         * @param {Object} prefs
         * @returns {Object}
         * @private
         */
        _selectActiveFeed: function(prefs) {
            var COOKIE_FEED_PREFIX = '_category_feed_';
            var allGameFeeds = config.get('gamesFeeds');
            var cookieFeedValue = prefs.categoriesCookieName;
            var storedCookieFeedName = $.cookie(COOKIE_FEED_PREFIX + cookieFeedValue);
            var userProfile = user.get('urlTemplates') || [];
            var profileFeeds = prefs.profileFeeds;
            var activeFeed;

            function findFeed(seed) {
                return _.find(allGameFeeds, function(feed) {
                    return feed.name === seed;
                });
            }

            if (storedCookieFeedName) {
                activeFeed = _.find(allGameFeeds, function(feed) {
                    return feed.name === storedCookieFeedName;
                });
            }

            if (activeFeed) {
                return activeFeed;
            }

            activeFeed = findFeed(prefs.categoriesFeedName);

            if (!userProfile.length || !profileFeeds || !prefs.enableUserProfile) {
                return activeFeed;
            }

            for (var i = 0; i < profileFeeds.length; i++) {
                if (segmentationHelper.isProfileAllowed(profileFeeds[i].urltemplates)) {
                    activeFeed = findFeed(profileFeeds[i].name) || activeFeed;
                    break;
                }
            }

            return activeFeed;
        },

        /**
         * Get category object by name
         * @param  {String} name - name of category
         * @param  {Array|Backbone.Collection} [categories] - categories collection to search in
         * @return {Object}
         */
        getCategory: function(name, categories) {
            categories = categories || this.categories && this.categories.toJSON();

            return util.deepFind(categories, 'subCategories', 'name', name);
        },

        getCategoryLocation: function() {
            return url.getLocationParameterByName('cat');
        },

        setCategoryLocation: function(category, push) {
            var path = url.getParametrizedURL({ cat: category });
            if (push) {
                historyUtil.pushState(null, null, path);
            } else {
                historyUtil.replaceState(null, null, path);
            }
        },

        /**
         * Add category if it does not exist
         * @param {Array|Backbone.Collection} categories
         * @param {String} name
         * @param {Object} [options]
         */
        _addCategory: function(categories, name, options) {
            categories = categories || this.categories;

            if (!this.getCategory(name, categories)) {
                options = _.defaults(options || {}, this._getPredefinedCategories()[name]);

                var orderBasedAdding = options.atStart ? Array.prototype.unshift : Array.prototype.push;
                var category = _.extend({}, options, {
                    name: name,
                    games: []
                });

                orderBasedAdding.call(categories, category);
            }
        },

        _getPredefinedCategories: function() {
            return {
                'recentlyplayed': {
                    name: 'recentlyplayed',
                    translation: 'playtech.mobile.games-info.recentlyplayed',
                    atStart: true
                },
                'favorites': {
                    name: 'favorites',
                    translation: 'playtech.mobile.games-info.favorites'
                }
            };
        },

        /**
         * Filter categories by configured games
         * @param {Array|Backbone.Collection} categories
         * @returns {Array}
         */
        _filterCategories: function(categories) {
            if (!categories) {
                return [];
            }

            var filterCategories = function(currentCategories) {
                return currentCategories.reduce(function(result, category) {
                    category.games = GamesInfoGameCollection.filterGames(category.games);
                    var hasGames = Boolean(category.games.length);
                    var hasGamesInSubCategories = false;

                    if (category.subCategories) {
                        category.subCategories = filterCategories(category.subCategories);
                        hasGamesInSubCategories = Boolean(category.subCategories.length);
                    }

                    if (hasGames || hasGamesInSubCategories) {
                        result.push(category);
                    }

                    return result;
                }, []);
            };

            return filterCategories(categories);
        }

    });
});
